/** The arithmetic of one figure, said so that it holds - for the record page's Calculation tab.
 *
 * **Built from the receipt and from nothing else** (rule 3): the receipt is the single rendering of a
 * figure's chain and this is a second *view* of the same fields, not a second source. It follows the
 * receipt drawer's own care (`ReceiptDrawer`), because each of its cases was a defect once:
 *
 * - **A market-based figure prices only the uncovered load.** A site that consumed 3,120,000 kWh
 *   with a REGO covering 2,600,000 of it has a figure of 520,000 times the factor; "3,120,000 times
 *   0.13096" is arithmetic that does not hold.
 * - **The operands are the ones the factor was multiplied by.** The engine converts what was entered
 *   into the unit the factor is published per before it prices, so 8,000 therms times a per-MMBtu
 *   factor is not the sum. The converted quantity is the operand; the entered one is its own row.
 * - **A refrigerant has no emission factor row**: its multiplier is each gas's GWP, so the figure is
 *   a sum of per-gas parts, not one product.
 * - **A factor that is carbon dioxide alone is not written as CO2e.**
 *
 * Nothing is rounded here but for display: the exact kilograms are the receipt's own string with
 * trailing zeros the engine did not state removed, and the tonnes are that string divided by 1,000
 * and rounded once, by `tonnes` (rule 5).
 */

import type { Receipt } from "../api/queries";
import { factorValue, quantity, statedQuantity, tonnes, unitLabel } from "../lib/format";

export type Operand = { key: string; label: string; value: string; unit: string };

export interface Calculation {
  /** The boxes left of the equals sign: usage and factor, or the per-gas parts. */
  operands: Operand[];
  /** `×` for a product, `+` for a sum of parts. */
  operator: "×" | "+";
  /** The exact kilograms, grouped and with the digits the engine stated. */
  exactKg: string;
  /** The same figure in tonnes, rounded once for display. */
  roundedT: string;
  /** The unit the exact figure is in. */
  kgUnit: string;
  /** One short line under the sum that the arithmetic needs to be true (a conversion, a coverage). */
  note: string | null;
}

const CO2_ONLY = "kgCO2";

/** The unit a factor's number counts: CO2e unless the factor says CO2 alone. */
export function kgUnitOf(receipt: Pick<Receipt, "factor">): string {
  return receipt.factor.unit_out === CO2_ONLY ? "kg CO₂ (CO₂ only)" : "kg CO₂e";
}

/** The exact figure: grouped thousands, and exactly the decimals the string states. */
export function exactKgOf(kg: string): string {
  const trimmed = factorValue(kg);
  const decimals = trimmed.includes(".") ? (trimmed.split(".")[1]?.length ?? 0) : 0;
  return quantity(kg, decimals);
}

export function calculationOf(receipt: Receipt): Calculation {
  const kgUnit = kgUnitOf(receipt);
  const base = { exactKg: exactKgOf(receipt.figure_kgco2e), roundedT: tonnes(receipt.figure_kgco2e, 2), kgUnit };

  const covered = receipt.components["covered_kWh"];
  const uncovered = receipt.components["uncovered_kWh"];
  const gasParts = Object.entries(receipt.components).filter(([key]) => !key.endsWith("_kWh"));

  /** A refrigerant has no emission-factor row: its multiplier is each gas's GWP, so it has no
   * publisher and no unit, and the figure is a sum of per-gas parts. (The drawer's `isGwp`.) Any other
   * figure with per-gas components is still usage times factor: the parts are how that product splits. */
  const isGwp =
    receipt.factor.value !== null &&
    !receipt.factor.publisher &&
    !receipt.factor.unit_in &&
    receipt.category.includes("fugitive");
  const byGas =
    gasParts.length > 1 && !isGwp
      ? `By gas: ${gasParts.map(([gas, kg]) => `${gas} ${exactKgOf(kg)}`).join(", ")} ${kgUnit}.`
      : null;

  // A refrigerant: several gases, each its own part, and the figure is their sum.
  if (isGwp && gasParts.length > 1 && covered === undefined) {
    return {
      ...base,
      operator: "+",
      operands: gasParts.map(([gas, kg]) => ({
        key: gas,
        label: gas,
        value: exactKgOf(kg),
        unit: "kg CO₂e",
      })),
      note: null,
    };
  }

  if (receipt.factor.value === null) {
    return { ...base, operator: "×", operands: [], note: null };
  }

  const factor: Operand = {
    key: "factor",
    label: "Factor",
    value: factorValue(receipt.factor.value),
    unit: `${kgUnit} per ${unitLabel(receipt.factor.unit_in ?? receipt.priced_unit ?? receipt.activity_unit)}`,
  };

  // A market-based figure: only the load no instrument covered is priced.
  if (covered !== undefined && Number(covered) > 0 && uncovered !== undefined) {
    return {
      ...base,
      operator: "×",
      operands: [{ key: "usage", label: "Uncovered load", value: statedQuantity(uncovered), unit: "kWh" }, factor],
      note: [
        `${statedQuantity(receipt.activity_quantity)} ${unitLabel(receipt.activity_unit)} used, less ${statedQuantity(covered)} kWh covered by an instrument at 0 ${kgUnit} per kWh.`,
        byGas,
      ]
        .filter(Boolean)
        .join(" "),
    };
  }

  const priced = receipt.priced_quantity ?? receipt.activity_quantity;
  const pricedUnit = receipt.priced_unit ?? receipt.activity_unit;
  const converted = pricedUnit !== receipt.activity_unit || Number(priced) !== Number(receipt.activity_quantity);
  return {
    ...base,
    operator: "×",
    operands: [{ key: "usage", label: "Usage", value: statedQuantity(priced), unit: unitLabel(pricedUnit) }, factor],
    note:
      [
        converted
          ? `${statedQuantity(receipt.activity_quantity)} ${unitLabel(receipt.activity_unit)} = ${statedQuantity(priced)} ${unitLabel(pricedUnit)}, the unit the factor is published per.`
          : null,
        byGas,
      ]
        .filter(Boolean)
        .join(" ") || null,
  };
}

/** One link of the trail, in the order a number is made. */
export interface TrailLink {
  key: string;
  label: string;
  met: boolean;
}

/** The trail of a figure, read from the receipt's evidence links.
 *
 * A figure recorded on a person's attestation has no bill, so its first link is **Basis stated** and
 * not *Bill stored*: a manual figure must never read as a documented one (ADR-0036). */
export function trailOf(receipt: Receipt): TrailLink[] {
  const met = new Set(receipt.evidence_met);
  const attested = receipt.reference_kind === "stated_basis";
  return [
    attested
      ? { key: "basis", label: "Basis stated", met: met.has("referenced") }
      : { key: "stored", label: "Bill stored", met: met.has("stored") },
    { key: "read", label: "Usage read", met: met.has("referenced") },
    { key: "factor", label: "Factor chosen", met: met.has("priced") },
    { key: "calculated", label: "Calculated", met: true },
    { key: "verified", label: "Verified", met: met.has("verified") },
    { key: "ledger", label: "Ledger", met: met.has("ledgered") || receipt.ledger.length > 0 },
  ];
}
